import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  DRBFMSession,
  DRBFMWizardStep,
  DRBFMInputData,
  DRBFMAnalysisProgress,
  SessionResultsResponse,
  FlowProgress,
  ApiError,
} from '../../types';
import { RootState } from '@store/index';

// ==============================================================================
// State Interface
// ==============================================================================

interface DRBFMState {
  // Session management
  sessions: DRBFMSession[];
  currentSession: DRBFMSession | null;
  sessionsLoading: boolean;
  sessionsError: string | null;
  sessionsTotal: number;
  sessionsPage: number;
  sessionsPageSize: number;

  // Analysis state
  analysisProgress: DRBFMAnalysisProgress;
  analysisResults: SessionResultsResponse | null;

  // UI state
  wizardStep: DRBFMWizardStep;
  inputData: DRBFMInputData;

  // Export state
  isExporting: boolean;
  exportError: string | null;
}

// ==============================================================================
// Initial State
// ==============================================================================

const initialInputData: DRBFMInputData = {
  itemName: '',
  title: '',
  oldSpecification: '',
  newSpecification: '',
  specOldDocumentId: undefined,
  specNewDocumentId: undefined,
  cadModelId: undefined,
  predefinedPartLabels: undefined,
};

const initialAnalysisProgress: DRBFMAnalysisProgress = {
  isRunning: false,
  currentFlow: 0,
  overallProgress: 0,
  flowStatuses: {},
  messages: [],
  error: undefined,
};

const initialState: DRBFMState = {
  // Session management
  sessions: [],
  currentSession: null,
  sessionsLoading: false,
  sessionsError: null,
  sessionsTotal: 0,
  sessionsPage: 1,
  sessionsPageSize: 20,

  // Analysis state
  analysisProgress: initialAnalysisProgress,
  analysisResults: null,

  // UI state
  wizardStep: 'input',
  inputData: initialInputData,

  // Export state
  isExporting: false,
  exportError: null,
};

// ==============================================================================
// Async Thunks
// ==============================================================================

export const fetchSessions = createAsyncThunk<
  { sessions: DRBFMSession[]; total: number },
  { limit?: number; offset?: number },
  { rejectValue: ApiError }
>('drbfm/fetchSessions', async (_params, { rejectWithValue }) => {
  try {
    // API call will be implemented in drbfm.service.ts
    throw new Error('Not implemented - use drbfm.service.ts');
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

export const fetchSession = createAsyncThunk<
  DRBFMSession,
  string,
  { rejectValue: ApiError }
>('drbfm/fetchSession', async (_sessionId, { rejectWithValue }) => {
  try {
    // API call will be implemented in drbfm.service.ts
    throw new Error('Not implemented - use drbfm.service.ts');
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

export const createSession = createAsyncThunk<
  DRBFMSession,
  { itemName: string; title?: string; specOldDocumentId?: string; specNewDocumentId?: string },
  { rejectValue: ApiError }
>('drbfm/createSession', async (_params, { rejectWithValue }) => {
  try {
    // API call will be implemented in drbfm.service.ts
    throw new Error('Not implemented - use drbfm.service.ts');
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

export const fetchSessionResults = createAsyncThunk<
  SessionResultsResponse,
  string,
  { rejectValue: ApiError }
>('drbfm/fetchSessionResults', async (_sessionId, { rejectWithValue }) => {
  try {
    // API call will be implemented in drbfm.service.ts
    throw new Error('Not implemented - use drbfm.service.ts');
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

export const deleteSession = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiError }
>('drbfm/deleteSession', async (_sessionId, { rejectWithValue }) => {
  try {
    // API call will be implemented in drbfm.service.ts
    throw new Error('Not implemented - use drbfm.service.ts');
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

// ==============================================================================
// Slice
// ==============================================================================

const drbfmSlice = createSlice({
  name: 'drbfm',
  initialState,
  reducers: {
    // Session management
    setCurrentSession: (state, action: PayloadAction<DRBFMSession | null>) => {
      state.currentSession = action.payload;
    },

    clearSessionsError: (state) => {
      state.sessionsError = null;
    },

    // Wizard step management
    setWizardStep: (state, action: PayloadAction<DRBFMWizardStep>) => {
      state.wizardStep = action.payload;
    },

    nextWizardStep: (state) => {
      if (state.wizardStep === 'input') {
        state.wizardStep = 'processing';
      } else if (state.wizardStep === 'processing') {
        state.wizardStep = 'results';
      }
    },

    previousWizardStep: (state) => {
      if (state.wizardStep === 'results') {
        state.wizardStep = 'processing';
      } else if (state.wizardStep === 'processing') {
        state.wizardStep = 'input';
      }
    },

    resetWizard: (state) => {
      state.wizardStep = 'input';
      state.inputData = initialInputData;
      state.analysisProgress = initialAnalysisProgress;
      state.analysisResults = null;
      state.currentSession = null;
    },

    // Input data management
    setInputData: (state, action: PayloadAction<Partial<DRBFMInputData>>) => {
      state.inputData = { ...state.inputData, ...action.payload };
    },

    setItemName: (state, action: PayloadAction<string>) => {
      state.inputData.itemName = action.payload;
    },

    setTitle: (state, action: PayloadAction<string>) => {
      state.inputData.title = action.payload;
    },

    setOldSpecification: (state, action: PayloadAction<string>) => {
      state.inputData.oldSpecification = action.payload;
    },

    setNewSpecification: (state, action: PayloadAction<string>) => {
      state.inputData.newSpecification = action.payload;
    },

    setSpecOldDocumentId: (state, action: PayloadAction<string | undefined>) => {
      state.inputData.specOldDocumentId = action.payload;
    },

    setSpecNewDocumentId: (state, action: PayloadAction<string | undefined>) => {
      state.inputData.specNewDocumentId = action.payload;
    },

    clearInputData: (state) => {
      state.inputData = initialInputData;
    },

    // Analysis progress management
    startAnalysis: (state) => {
      state.analysisProgress = {
        isRunning: true,
        currentFlow: 0,
        overallProgress: 0,
        flowStatuses: {},
        messages: [],
        error: undefined,
      };
      state.wizardStep = 'processing';
    },

    updateAnalysisProgress: (state, action: PayloadAction<Partial<DRBFMAnalysisProgress>>) => {
      state.analysisProgress = { ...state.analysisProgress, ...action.payload };
    },

    setCurrentFlow: (state, action: PayloadAction<number>) => {
      state.analysisProgress.currentFlow = action.payload;
    },

    setOverallProgress: (state, action: PayloadAction<number>) => {
      state.analysisProgress.overallProgress = action.payload;
    },

    updateFlowStatus: (state, action: PayloadAction<{ flow: keyof FlowProgress; status: FlowProgress[keyof FlowProgress] }>) => {
      const { flow, status } = action.payload;
      state.analysisProgress.flowStatuses[flow] = status;
    },

    addAnalysisMessage: (state, action: PayloadAction<string>) => {
      state.analysisProgress.messages.push(action.payload);
    },

    setAnalysisError: (state, action: PayloadAction<string>) => {
      state.analysisProgress.error = action.payload;
      state.analysisProgress.isRunning = false;
    },

    completeAnalysis: (state) => {
      state.analysisProgress.isRunning = false;
      state.analysisProgress.overallProgress = 1;
      state.wizardStep = 'results';
    },

    cancelAnalysis: (state) => {
      state.analysisProgress.isRunning = false;
      state.wizardStep = 'input';
    },

    // Results management
    setAnalysisResults: (state, action: PayloadAction<SessionResultsResponse | null>) => {
      state.analysisResults = action.payload;
    },

    clearAnalysisResults: (state) => {
      state.analysisResults = null;
    },

    // Export state management
    startExport: (state) => {
      state.isExporting = true;
      state.exportError = null;
    },

    completeExport: (state) => {
      state.isExporting = false;
    },

    setExportError: (state, action: PayloadAction<string>) => {
      state.isExporting = false;
      state.exportError = action.payload;
    },

    clearExportError: (state) => {
      state.exportError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch sessions
    builder
      .addCase(fetchSessions.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.sessionsLoading = false;
        state.sessions = action.payload.sessions;
        state.sessionsTotal = action.payload.total;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError = action.payload?.message || 'Failed to fetch sessions';
      });

    // Fetch single session
    builder
      .addCase(fetchSession.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.sessionsLoading = false;
        state.currentSession = action.payload;
      })
      .addCase(fetchSession.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError = action.payload?.message || 'Failed to fetch session';
      });

    // Create session
    builder
      .addCase(createSession.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(createSession.fulfilled, (state, action) => {
        state.sessionsLoading = false;
        state.currentSession = action.payload;
        state.sessions.unshift(action.payload);
      })
      .addCase(createSession.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError = action.payload?.message || 'Failed to create session';
      });

    // Fetch session results
    builder
      .addCase(fetchSessionResults.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(fetchSessionResults.fulfilled, (state, action) => {
        state.sessionsLoading = false;
        state.analysisResults = action.payload;
      })
      .addCase(fetchSessionResults.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError = action.payload?.message || 'Failed to fetch results';
      });

    // Delete session
    builder
      .addCase(deleteSession.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.sessionsLoading = false;
        state.sessions = state.sessions.filter((s) => s.id !== action.payload);
        if (state.currentSession?.id === action.payload) {
          state.currentSession = null;
        }
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError = action.payload?.message || 'Failed to delete session';
      });
  },
});

// ==============================================================================
// Actions
// ==============================================================================

export const {
  // Session management
  setCurrentSession,
  clearSessionsError,
  // Wizard step management
  setWizardStep,
  nextWizardStep,
  previousWizardStep,
  resetWizard,
  // Input data management
  setInputData,
  setItemName,
  setTitle,
  setOldSpecification,
  setNewSpecification,
  setSpecOldDocumentId,
  setSpecNewDocumentId,
  clearInputData,
  // Analysis progress management
  startAnalysis,
  updateAnalysisProgress,
  setCurrentFlow,
  setOverallProgress,
  updateFlowStatus,
  addAnalysisMessage,
  setAnalysisError,
  completeAnalysis,
  cancelAnalysis,
  // Results management
  setAnalysisResults,
  clearAnalysisResults,
  // Export state management
  startExport,
  completeExport,
  setExportError,
  clearExportError,
} = drbfmSlice.actions;

// ==============================================================================
// Selectors
// ==============================================================================

// Session selectors
export const selectSessions = (state: RootState) => state.drbfm.sessions;
export const selectCurrentSession = (state: RootState) => state.drbfm.currentSession;
export const selectSessionsLoading = (state: RootState) => state.drbfm.sessionsLoading;
export const selectSessionsError = (state: RootState) => state.drbfm.sessionsError;
export const selectSessionsTotal = (state: RootState) => state.drbfm.sessionsTotal;

// Wizard selectors
export const selectWizardStep = (state: RootState) => state.drbfm.wizardStep;
export const selectInputData = (state: RootState) => state.drbfm.inputData;

// Analysis selectors
export const selectAnalysisProgress = (state: RootState) => state.drbfm.analysisProgress;
export const selectIsAnalyzing = (state: RootState) => state.drbfm.analysisProgress.isRunning;
export const selectCurrentFlowNumber = (state: RootState) => state.drbfm.analysisProgress.currentFlow;
export const selectOverallProgress = (state: RootState) => state.drbfm.analysisProgress.overallProgress;
export const selectFlowStatuses = (state: RootState) => state.drbfm.analysisProgress.flowStatuses;
export const selectAnalysisMessages = (state: RootState) => state.drbfm.analysisProgress.messages;
export const selectAnalysisError = (state: RootState) => state.drbfm.analysisProgress.error;

// Results selectors
export const selectAnalysisResults = (state: RootState) => state.drbfm.analysisResults;
export const selectDesignChanges = (state: RootState) => state.drbfm.analysisResults?.designChanges ?? [];
export const selectFailureModes = (state: RootState) => state.drbfm.analysisResults?.failureModes ?? [];
export const selectCountermeasures = (state: RootState) => state.drbfm.analysisResults?.countermeasures ?? [];

// Export selectors
export const selectIsExporting = (state: RootState) => state.drbfm.isExporting;
export const selectExportError = (state: RootState) => state.drbfm.exportError;

// Derived selectors
export const selectCanStartAnalysis = (state: RootState) => {
  const { itemName, oldSpecification, newSpecification } = state.drbfm.inputData;
  return itemName.trim() !== '' && oldSpecification.trim() !== '' && newSpecification.trim() !== '';
};

export const selectSessionById = (sessionId: string) => (state: RootState) =>
  state.drbfm.sessions.find((s) => s.id === sessionId);

export const selectCompletedSessions = (state: RootState) =>
  state.drbfm.sessions.filter((s) => s.status === 'completed');

export const selectDraftSessions = (state: RootState) =>
  state.drbfm.sessions.filter((s) => s.status === 'draft');

export default drbfmSlice.reducer;
